/**
 * Панель «Поиск» экранов библиотек, библиотеки и «Всех референсов».
 *
 * Запрос — описание словами (строка «Описание: что на фото», по-русски: сервер переводит), картинка
 * (Ctrl-V, перетаскивание, выбор файла) или платье «Наших фото» (`DressPickerDialog`; окно
 * мульти-выбора — берём первое отмеченное, поиск по одному платью). Источник один: выбор другого
 * заменяет прежний. Текст режется по краям, пустой — не запрос (максимум 200 знаков, как у сервера).
 * Выбор поиск не запускает — его запускает «Найти» (`searchReducer` в `lib/similar.ts`:
 * выбранное и отправленное порознь, прежняя выдача видна, пока не нажали «Найти» снова).
 * Отправленное платье пишется в адрес (`?similar_dress=73` — ссылкой можно поделиться, она
 * открывает панель и сразу ищет), описание — так же (`?similar_text=замок`; каждый стирает другой),
 * картинка — только в состоянии панели. У выдачи по описанию процентов нет, над ней подпись `textCaption`. Пока панель открыта,
 * вставка и перетаскивание картинок идут сюда; экран библиотеки свои обработчики загрузки в это
 * время глушит сам.
 *
 * Выдача — в кэше react-query под `library` (`libraryKeys.similar`): обозреватель патчит её
 * строки как любой срез, удаление и перенос её перезапрашивают. Экрану отдаётся `onResult`:
 * строки по убыванию score, ключ кэша и подпись запроса; `null` — выдачи нет.
 */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2, ScanSearch, Shirt, Upload, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import { btn, btnGhost, btnPrimary, ErrorLine, input, Panel } from "@/components/trends/ui";
import { flatLibraries, libraryKeys, loadLibraries, plural, searchSimilar } from "@/lib/library";
import { mediaUrl, num } from "@/lib/media";
import { groupByModel } from "@/lib/models";
import { dressTitle, loadPhotos, photoKeys } from "@/lib/photos";
import {
  initialSearch,
  reportFor,
  retrySimilar,
  searchReducer,
  sourceOf,
  similarView,
  submitKind,
  textCaption,
  type SearchChoice,
  type SimilarView,
} from "@/lib/similar";

export type SimilarFound = { view: SimilarView; queryKey: readonly unknown[]; label: string };

const ACCEPT = "image/jpeg,image/png,image/webp";
const NO_IDS: readonly number[] = [];
const NO_NOTES: ReadonlyMap<number, string> = new Map();

const MAX_TEXT = 200;

/** Текст запроса из адреса или строки: по краям режем, пустой — нет запроса. */
const cleanText = (raw: string | null): string | null => raw?.trim() || null;

function positiveInt(raw: string | null): number | null {
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : null;
}

export function PhotoSearchPanel({
  libraryId,
  onResult,
  onClose,
}: {
  /** Экран библиотеки: поиск только в ней. Нет — выбор библиотеки в панели («Библиотеки», «Все референсы»). */
  libraryId?: number;
  onResult: (found: SimilarFound | null) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const dressParam = positiveInt(params.get("similar_dress"));
  const textParam = cleanText(params.get("similar_text"));
  const [pickedLibrary, setPickedLibrary] = useState<number | null>(null);
  const scope = libraryId ?? pickedLibrary;
  // Ссылка с платьем — сразу отправленный запрос: поделились ссылкой — выдача без лишнего клика.
  const [state, dispatch] = useReducer(searchReducer<File>, undefined, () =>
    initialSearch<File>(dressParam, libraryId ?? null, dressParam === null ? textParam : null),
  );
  const [draft, setDraft] = useState(dressParam === null ? (textParam ?? "") : "");
  const { chosen, submitted } = state;
  const counter = useRef(0);
  const [picker, setPicker] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Запрос в адресе: платье или описание (платье главнее, если вписаны оба). `wrote` — что там лежит по нашей
  // записи: чужая смена (ссылка, «назад») — новый запрос или сброс.
  const urlKey = dressParam !== null ? `d:${dressParam}` : textParam !== null ? `t:${textParam}` : "";
  const wrote = useRef(urlKey);
  useEffect(() => {
    if (urlKey === wrote.current) return;
    wrote.current = urlKey;
    if (dressParam !== null) {
      setDraft("");
      dispatch({ type: "deeplink", dressId: dressParam, scope });
    } else if (textParam !== null) {
      setDraft(textParam);
      dispatch({ type: "deeplink", text: textParam, scope });
    } else {
      setDraft("");
      dispatch({ type: "reset" });
    }
    // Смена одного scope сюда не проходит: адрес тот же, что записан.
  }, [urlKey, dressParam, textParam, scope]);
  /** Новый запрос: прежняя папка выдачи (`model`) к нему не относится. Платье и описание в адресе взаимно исключают друг друга. */
  const writeQuery = (dress: number | null, text: string | null) => {
    const key = dress !== null ? `d:${dress}` : text !== null ? `t:${text}` : "";
    if (key === wrote.current && !params.has("model")) return;
    wrote.current = key;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (dress === null) next.delete("similar_dress");
      else next.set("similar_dress", String(dress));
      if (text === null || dress !== null) next.delete("similar_text");
      else next.set("similar_text", text);
      next.delete("model");
      return next;
    });
  };

  const source = sourceOf(submitted);
  const sentScope = submitted?.scope ?? null;
  const queryKey = useMemo(() => libraryKeys.similar(source ?? "", sentScope), [source, sentScope]);

  const search = useQuery({
    queryKey,
    queryFn: async (): Promise<SimilarView> => {
      const choice = submitted?.choice;
      if (!choice) throw new Error("Нечего искать.");
      const got = await searchSimilar({
        ...(choice.kind === "file" ? { file: choice.file } : choice.kind === "dress" ? { dressId: choice.id } : { text: choice.text }),
        ...(sentScope !== null ? { libraryId: sentScope } : {}),
      });
      return similarView(got);
    },
    enabled: source !== null,
    // Новый запрос: прежняя выдача (и выделение на ней) остаётся, пока не придёт ответ.
    placeholderData: keepPreviousData,
    retry: retrySimilar,
    // Картинку заново шлём только по перезапросу после записи, не по фокусу окна.
    staleTime: Infinity,
  });

  const needDresses = picker || chosen?.kind === "dress" || submitted?.choice.kind === "dress";
  const dresses = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, enabled: needDresses });
  const libs = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries, enabled: libraryId === undefined });
  const dressOf = (id: number) => dresses.data?.dresses.find((d) => d.id === id) ?? null;
  const titleOf = (choice: SearchChoice<File>) =>
    choice.kind === "file" ? "по картинке" : choice.kind === "text" ? `по описанию «${choice.text}»` : `по платью «${dressTitle(dressOf(choice.id) ?? { id: choice.id, name: null })}»`;

  const shown = reportFor(source, search.data, search.isPlaceholderData);
  // Выдача по описанию подписана переводом («по описанию «замок» → …»); пока ответа нет — только описанием.
  const caption = shown && shown !== "keep" ? textCaption(shown) : "";
  const label = submitted ? (submitted.choice.kind === "text" && caption ? caption : titleOf(submitted.choice)) : "";
  const chosenDress = chosen?.kind === "dress" ? dressOf(chosen.id) : null;
  const preview =
    chosen?.kind === "file" ? chosen.url : chosenDress?.cover?.thumb_path ? mediaUrl(chosenDress.cover.thumb_path) : null;

  // Слушатели окна и эффект выдачи берут свежие функции из ref — вешаются один раз.
  const report = useRef(onResult);
  useLayoutEffect(() => {
    report.current = onResult;
  }, [onResult]);
  const data = source !== null ? search.data : undefined;
  const verdict = shown;
  useEffect(() => {
    // Прежняя выдача на время нового запроса — экрану её не пересдаём под чужим ключом кэша.
    if (verdict === "keep") return;
    report.current(verdict ? { view: verdict, queryKey, label } : null);
  }, [verdict, queryKey, label]);
  useEffect(() => () => report.current(null), []);

  // Превью картинки — object URL; сменили выбор или закрыли — освобождаем.
  // Отправленной картинке URL не нужен: запрос шлёт сам файл.
  const chosenUrl = chosen?.kind === "file" ? chosen.url : null;
  useEffect(() => {
    if (!chosenUrl) return;
    return () => URL.revokeObjectURL(chosenUrl);
  }, [chosenUrl]);

  const takeFile = useRef<(files: File[]) => void>(() => undefined);
  useLayoutEffect(() => {
    takeFile.current = (files) => {
      const file = files[0];
      if (!file) return;
      counter.current += 1;
      setDraft("");
      dispatch({ type: "choose", choice: { kind: "file", n: counter.current, file, url: URL.createObjectURL(file) } });
    };
  });
  const pickDress = (ids: number[]) => {
    const first = ids[0];
    if (first === undefined) return;
    setDraft("");
    dispatch({ type: "choose", choice: { kind: "dress", id: first } });
  };

  const kind = submitKind(state, scope);
  const busy = search.isFetching;
  const typeText = (value: string) => {
    setDraft(value);
    const text = cleanText(value);
    if (text !== null) dispatch({ type: "choose", choice: { kind: "text", text } });
    else if (chosen?.kind === "text") dispatch({ type: "clear" });
  };
  // Тот же запрос ещё раз — только после ошибки; новый — всегда, кроме идущего поиска.
  const canSubmit = !busy && (kind === "new" || (kind === "same" && search.isError));
  const submit = () => {
    if (!canSubmit || !chosen) return;
    if (kind === "same") {
      void search.refetch();
      return;
    }
    dispatch({ type: "submit", scope });
    writeQuery(chosen.kind === "dress" ? chosen.id : null, chosen.kind === "text" ? chosen.text : null);
  };
  const reset = () => {
    // Вердикт, теги и корзину на выдаче патчили только её кэш — срез экрана перечитываем.
    // Саму выдачу не трогаем: она активна до сброса источника, и инвалидация заново послала бы поиск.
    if (source !== null)
      void queryClient.invalidateQueries({ queryKey: ["library"], predicate: (q) => q.queryKey[1] !== "similar" });
    dispatch({ type: "reset" });
    setDraft("");
    writeQuery(null, null);
  };

  // Вставка и перетаскивание — на всё окно, как загрузка в библиотеку: пока панель открыта, картинка — запрос.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      takeFile.current(Array.from(e.dataTransfer?.files ?? []));
    };
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      // Текст в поле заметки вставляется как обычно — перехватываем только картинки.
      if (!files.length) return;
      e.preventDefault();
      takeFile.current(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", onPaste);
    };
  }, []);

  const grouped = useMemo(() => (data ? groupByModel(data.items) : null), [data]);
  const models = grouped?.folders.length ?? 0;
  const found = data
    ? models > 0
      ? `Найдено ${num(models)} ${plural(models, "модель", "модели", "моделей")}`
      : `Найдено ${num(data.items.length)} фото`
    : "";

  return (
    <Panel
      title="Поиск"
      annot="поиск"
      aside={
        <button
          type="button"
          className={btnGhost}
          onClick={() => {
            reset();
            onClose();
          }}
          aria-label="Закрыть поиск"
          title="Закрыть поиск"
        >
          <X size={14} />
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-3 px-3 py-2">
        <input
          type="text"
          className={`${input} w-72 max-w-full`}
          placeholder="Описание: что на фото"
          aria-label="Описание: что на фото"
          maxLength={MAX_TEXT}
          value={draft}
          onChange={(e) => typeText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          data-annot="описание для поиска"
        />
        {chosen && chosen.kind !== "text" ? (
          <div className="relative shrink-0" data-annot="запрос поиска">
            {preview ? (
              <img
                src={preview}
                alt="Запрос"
                className="size-16 rounded-[var(--radius-ui)] border border-border object-cover"
              />
            ) : (
              <div className="grid size-16 place-items-center rounded-[var(--radius-ui)] border border-border bg-bg-subtle text-fg-faint">
                <Shirt size={18} />
              </div>
            )}
            <button
              type="button"
              className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full border border-border bg-bg-elevated text-fg-muted shadow-sm transition-colors hover:text-fg"
              onClick={() => dispatch({ type: "clear" })}
              aria-label="Убрать запрос"
              title="Убрать"
            >
              <X size={11} />
            </button>
          </div>
        ) : null}
        {chosen && chosen.kind !== "text" ? <span className="text-[12.5px] text-fg-muted">{titleOf(chosen)}</span> : null}
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            takeFile.current(Array.from(e.target.files ?? []));
            e.target.value = ""; // тот же файл ещё раз — тоже событие
          }}
        />
        <button type="button" className={btn} onClick={() => fileInput.current?.click()}>
          <ImagePlus size={14} />
          Выбрать файл
        </button>
        <button type="button" className={btn} onClick={() => setPicker(true)}>
          <Shirt size={14} />
          Платье из Наших фото
        </button>
        {libraryId === undefined ? (
          <select
            className={input}
            aria-label="Где искать"
            value={pickedLibrary ?? ""}
            onChange={(e) => setPickedLibrary(positiveInt(e.target.value))}
          >
            <option value="">во всех библиотеках</option>
            {flatLibraries(libs.data).map((l) => (
              <option key={l.id} value={l.id}>
                в библиотеке «{l.name}»
              </option>
            ))}
          </select>
        ) : null}
        <button
          type="button"
          className={btnPrimary}
          onClick={submit}
          disabled={!canSubmit}
          title={kind === "none" ? "Сначала введите описание, выберите картинку или платье" : undefined}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
          {busy ? "Ищу…" : "Найти"}
        </button>
        <span className="text-[12px] text-fg-faint">или вставьте картинку Ctrl-V, или перетащите сюда</span>
      </div>

      {submitted !== null ? (
        <div
          className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2 text-[12.5px]"
          role="status"
        >
          <span className="text-fg-muted">{label}</span>
          {busy ? <span className="text-fg-faint">{submitted.choice.kind === "text" ? "ищу…" : "ищу похожие…"}</span> : null}
          {data ? (
            <span className="tnum" title={`референсов в поиске: ${num(data.total)}`}>
              {found}
              {data.missing > 0 ? ` · ${num(data.missing)} фото ещё без вектора` : ""}
            </span>
          ) : null}
          <button type="button" className={btnGhost} onClick={reset}>
            сбросить
          </button>
        </div>
      ) : null}
      {search.isError && source !== null ? (
        <div className="px-3 pb-2">
          <ErrorLine error={search.error} />
        </div>
      ) : null}
      {dresses.isError ? (
        <div className="px-3 pb-2">
          <ErrorLine error={dresses.error} />
        </div>
      ) : null}

      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={dresses.data}
        taken={NO_IDS}
        annotate={NO_NOTES}
        triedAll={null}
        onAdd={pickDress}
      />

      {dragging ? (
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur-[2px]">
          <div className="grid justify-items-center gap-2 rounded-[var(--radius-card)] border-2 border-dashed border-accent bg-bg-elevated px-10 py-8 text-[14px] text-fg">
            <Upload size={28} className="text-accent" />
            Отпустите — картинка станет запросом поиска
          </div>
        </div>
      ) : null}
    </Panel>
  );
}
